import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { UtmTrackingService } from './core/services/utm-tracking.service';
import { FbclidService } from './core/services/fbclid.service';
import { DynamicManifestService } from './core/services/dynamic-manifest.service';
import { OperationalNoTranslateService } from './core/services/operational-no-translate.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastModule],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App {
  protected title = 'PidePaYa.Web';
  private translate = inject(TranslateService);
  private utmTracking = inject(UtmTrackingService);
  private fbclid = inject(FbclidService);
  private dynamicManifest = inject(DynamicManifestService);
  private operationalNoTranslate = inject(OperationalNoTranslateService);

  constructor() {
    this.translate.setFallbackLang('es');
    this.translate.use('es');
    this.stripOgSentinel();
    // Captura los UTM del query string apenas el SPA arranca y se mantiene
    // escuchando navegaciones. La atribucion vive en sessionStorage por
    // slug y se lee al crear orden en el menu publico.
    this.utmTracking.init();
    // Mismo patron para el fbclid del click de Meta: se captura apenas entra (antes de
    // que el cliente navegue y lo pierda) y sobrevive al adblocker que tumba el SDK.
    this.fbclid.init();
    // En pantallas operativas (/<slug>/pos, /mesero, /cocina, /barra, /kiosco)
    // reescribe el manifest.webmanifest para que "Agregar a pantalla de inicio"
    // de Chrome cree un shortcut que abre la pantalla exacta — sin esto, el
    // start_url default `/` siempre lleva al catalogo global.
    this.dynamicManifest.init();
    // En pantallas operativas (/<slug>/pos, /mesero, /cocina, /barra) bloquea la
    // traduccion automatica del navegador, que mutila datos como el metodo "Nequi"
    // ("De ninguna manera"). Cubre tambien los popups de PrimeNG montados en <body>.
    this.operationalNoTranslate.init();
  }

  // ?_og=1 es un sentinel que pone OpenGraphController para que el .htaccess no
  // vuelva a mandar el request a og.php (rompe el loop de redirects en falsos
  // positivos del UA). El SPA no lo necesita; lo limpiamos del address bar para
  // que el usuario no lo vea ni lo comparta.
  private stripOgSentinel(): void {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('_og')) return;
    params.delete('_og');
    const queryString = params.toString();
    const newSearch = queryString ? `?${queryString}` : '';
    const newUrl = window.location.pathname + newSearch + window.location.hash;
    window.history.replaceState(window.history.state, '', newUrl);
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(event: Event): void {
    // Allow Enter on login/register pages
    const path = window.location.pathname;
    if (path.includes('/login') || path.includes('/register')) return;

    const target = event.target as HTMLElement;
    const tag = target.tagName.toLowerCase();
    // Allow Enter in textareas and contenteditable, block in all other inputs/forms
    if (tag === 'textarea' || target.isContentEditable) return;
    if (tag === 'input' || tag === 'select') {
      event.preventDefault();
    }
  }
}
